const CACHE_KEY_PREFIX = 'shopInsuranceFilterDefaults';

function cacheKey(advisorId: string): string {
  return `${CACHE_KEY_PREFIX}:${advisorId}`;
}

export function getCachedFilterDefaults(
  advisorId: string,
): ShopInsuranceFilterDefaults | null {
  try {
    const raw = localStorage.getItem(cacheKey(advisorId));
    if (!raw) return null;
    return JSON.parse(raw) as ShopInsuranceFilterDefaults;
  } catch {
    return null;
  }
}

type SavedInflationProtection =
  | number
  | { type?: string; combined?: number[]; aob?: number[]; cob?: number[] }
  | null
  | undefined;

function lowerBound(range?: number[]): number | null {
  return typeof range?.[0] === 'number' ? range[0] : null;
}

/**
 * Defaults saved before inflation protection became a single minimum hold an AOB/COB range
 * object, and which of its ranges carries the advisor's intent depends on the mode it was
 * saved in:
 *
 * - Combined mode drove `combined` directly, so its lower bound is the minimum.
 * - Separate mode bound only the `aob` and `cob` sliders; `combined` was left at its
 *   initial [0, 5] and saved stale. Reading it would migrate a real constraint to 0 and
 *   silently drop the filter, so separate defaults are derived from AOB/COB alone.
 *
 * Separate mode required both sides to sit in range where a minimum only asks for one, so
 * the higher of the two lower bounds is the closest equivalent — it keeps the restriction
 * the advisor saved rather than widening to the least restrictive side.
 */
export function toMinimumInflationPercent(saved: SavedInflationProtection): number | null {
  if (saved == null) return null;
  if (typeof saved === 'number') return saved;

  const sideBounds = [lowerBound(saved.aob), lowerBound(saved.cob)].filter(
    (value): value is number => value !== null,
  );
  const highestSideBound = sideBounds.length ? Math.max(...sideBounds) : null;

  if (saved.type === 'separate') return highestSideBound;

  return lowerBound(saved.combined) ?? highestSideBound;
}

/**
 * The legacy AOB/COB range shape, for the `inflationProtectionMinimum`-off path. A default
 * saved as a minimum N becomes the range [N, MAX_INFLATION_PERCENT], which is how the range
 * UI expressed "at least N" — the two filters agree on that reading. Returns null when
 * nothing was saved so callers fall back to their own default.
 */
export function toLegacyInflationRange(
  saved: SavedInflationProtection,
  maxPercent: number,
): { type: string; combined: number[]; aob: number[]; cob: number[] } | null {
  if (saved == null) return null;

  if (typeof saved === 'number') {
    const range = [saved, maxPercent];
    return { type: 'combined', combined: range, aob: range, cob: range };
  }

  if (!saved.combined && !saved.aob && !saved.cob) return null;

  const fallback = [0, maxPercent];
  return {
    type: saved.type ?? 'combined',
    combined: saved.combined ?? fallback,
    aob: saved.aob ?? fallback,
    cob: saved.cob ?? fallback,
  };
}

export function setCachedFilterDefaults(
  advisorId: string,
  defaults: ShopInsuranceFilterDefaults,
): void {
  try {
    localStorage.setItem(cacheKey(advisorId), JSON.stringify(defaults));
  } catch {
    // quota exceeded or private browsing — silently ignore
  }
}
